import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Search, MapPin, User, ShoppingBag, LogOut, ChevronDown, UserCircle, Settings, Heart, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useServices } from '../hooks/useFirestoreServices';
import logo from '../assets/images/logo.png';
import MobileAccountDialog from './MobileAccountDialog';
import CategoryNavbar from './CategoryNavbar';

  export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileAccountDialog, setShowMobileAccountDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [userLocation, setUserLocation] = useState('Detecting location...');
  const [locationLoading, setLocationLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showHeader, setShowHeader] = useState(true);
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const { services: allServices } = useServices();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // Handle scroll for header style changes
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Header always visible on mobile - no hide/show
      setShowHeader(true);
      
      setIsScrolled(currentScrollY > 10);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  // Auto-detect location on component mount
  useEffect(() => {
    const detectLocation = async () => {
      try {
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              
              // Use reverse geocoding to get city name
              try {
                const response = await fetch(
                  `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
                );
                const data = await response.json();
                const city = data.city || data.locality || data.countryName || 'Unknown Location';
                setUserLocation(city);
              } catch (error) {
                // Fallback to major cities in India
                setUserLocation('Bilaspur, Himachal Pradesh');
              }
              setLocationLoading(false);
            },
            (error) => {
              console.log('Location access denied:', error);
              setUserLocation('Bilaspur, Himachal Pradesh'); // Default location
              setLocationLoading(false);
            }
          );
        } else {
          setUserLocation('Bilaspur, Himachal Pradesh');
          setLocationLoading(false);
        }
      } catch (error) {
        setUserLocation('Bilaspur, Himachal Pradesh');
        setLocationLoading(false);
      }
    };

    detectLocation();
  }, []);

  // Update search suggestions when search term changes
  useEffect(() => {
    if (searchTerm.trim() && allServices.length > 0) {
      const query = searchTerm.toLowerCase();
      const activeServices = allServices.filter(service => service.isActive !== false);
      
      const matchedServices: typeof allServices = [];
      
      // Search in service names and descriptions
      activeServices.forEach(service => {
        if (service.name.toLowerCase().includes(query) || 
            service.description.toLowerCase().includes(query) ||
            service.category?.toLowerCase().includes(query)) {
          matchedServices.push(service);
        }
      });
      
      setSearchSuggestions(matchedServices.slice(0, 8).map(s => s.name));
    } else {
      setSearchSuggestions([]);
    }
  }, [searchTerm, allServices]);

  const handleSearch = (query?: string) => {
    const searchQuery = query || searchTerm;
    if (searchQuery.trim()) {
      navigate(`/services?search=${encodeURIComponent(searchQuery)}`);
      setSearchTerm('');
      setShowSearchSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestionName: string) => {
    // Find the service by name and navigate directly to its detail page
    const service = allServices.find(s => s.name === suggestionName);
    if (service) {
      // Close dropdowns first
      setSearchTerm('');
      setShowSearchSuggestions(false);
      setIsMenuOpen(false);
      // Navigate to service detail page
      navigate(`/services/${service.id}`);
    } else {
      // If service not found, navigate to all services page
      setSearchTerm('');
      setShowSearchSuggestions(false);
      setIsMenuOpen(false);
      navigate('/services');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if click is on a search suggestion button - if so, don't close
      if (target.closest('button[data-suggestion]')) {
        return;
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchSuggestions(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    // Use 'click' instead of 'mousedown' to allow button clicks to fire first
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <>
      <header className="bg-white shadow-lg sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-primary-600 text-white py-1.5 md:py-2">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center text-xs md:text-sm">
            <div className="flex items-center space-x-2 md:space-x-4">
              <a 
                href="https://wa.me/917018401540?text=Hi%2C%20I%20would%20like%20to%20know%20more%20about%20your%20services" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center hover:opacity-80 transition-opacity"
              >
                <MessageCircle className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">WhatsApp Us</span>
                <span className="sm:hidden">Chat</span>
              </a>
              <div className="flex items-center">
                <MapPin className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                <span className="flex items-center truncate max-w-[120px] sm:max-w-none">
                  {locationLoading ? (
                    <>
                      <div className="animate-spin h-2.5 w-2.5 md:h-3 md:w-3 border border-white border-t-transparent rounded-full mr-1"></div>
                      <span className="hidden sm:inline">Detecting location...</span>
                      <span className="sm:hidden">Finding...</span>
                    </>
                  ) : (
                    <span className="truncate">{userLocation}</span>
                  )}
                </span>
              </div>
            </div>
            
            {/* Vertical Divider */}
            <div className="hidden md:block h-6 w-px bg-white/30 mx-4"></div>
            
            {/* Marquee News Banner */}
            <div className="hidden md:block flex-1 overflow-hidden">
              <div className="relative h-full flex items-center">
                <style>{`
                  @keyframes marquee {
                    0% { transform: translateX(0%); }
                    100% { transform: translateX(-50%); }
                  }
                  .animate-marquee {
                    animation: marquee 30s linear infinite;
                  }
                  .animate-marquee:hover {
                    animation-play-state: paused;
                  }
                `}</style>
                <div className="flex animate-marquee whitespace-nowrap">
                  {/* First set of news items */}
                  <div className="flex items-center">
                    <span className="bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold mr-3">NEWS</span>
                    <span className="text-white text-sm mr-6">🎉 Experience doorstep service like never before!</span>
                    <span className="bg-white text-red-600 px-2 py-0.5 rounded text-xs font-bold mr-3">OFFER</span>
                    <span className="text-white text-sm mr-6">💰 Get 20% OFF on your first booking - Use code: FIRST20</span>
                    <span className="bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold mr-3">UPDATE</span>
                    <span className="text-white text-sm mr-6">⚡ Same-day service available in your area!</span>
                    <span className="bg-white text-red-600 px-2 py-0.5 rounded text-xs font-bold mr-3">NEW</span>
                    <span className="text-white text-sm mr-6">🌟 5000+ satisfied customers and counting</span>
                  </div>
                  {/* Duplicate set for seamless loop */}
                  <div className="flex items-center">
                    <span className="bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold mr-3">NEWS</span>
                    <span className="text-white text-sm mr-6">🎉 Experience doorstep service like never before!</span>
                    <span className="bg-white text-red-600 px-2 py-0.5 rounded text-xs font-bold mr-3">OFFER</span>
                    <span className="text-white text-sm mr-6">💰 Get 20% OFF on your first booking - Use code: FIRST20</span>
                    <span className="bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold mr-3">UPDATE</span>
                    <span className="text-white text-sm mr-6">⚡ Same-day service available in your area!</span>
                    <span className="bg-white text-red-600 px-3 py-1 rounded-md font-bold text-sm mr-4">NEW</span>
                    <span className="text-white mr-8">🌟 5000+ satisfied customers and counting</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center py-4">
          {/* Logo - Left */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img 
              src={logo} 
              alt="RAPSAPP Logo" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Search Bar - Center */}
          <div className="hidden md:flex flex-1 max-w-lg mx-8 relative" ref={searchRef}>
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Search for beauty, cleaning, repairs..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSearchSuggestions(true);
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                onFocus={() => setShowSearchSuggestions(true)}
                className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              />
              <button
                onClick={() => handleSearch()}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-600 text-white p-2 rounded-md hover:bg-primary-700"
              >
                <Search className="h-4 w-4" />
              </button>

              {/* Search Suggestions */}
              {showSearchSuggestions && searchTerm && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl mt-1 z-50 max-h-96 overflow-y-auto">
                  {searchSuggestions.map((suggestionName, index) => {
                    const service = allServices.find(s => s.name === suggestionName);
                    return service ? (
                      <button
                        key={index}
                        data-suggestion="true"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSuggestionClick(suggestionName);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors border-b border-gray-100 last:border-b-0 first:rounded-t-lg last:rounded-b-lg flex items-center gap-3"
                      >
                        {service.image ? (
                          <img 
                            src={service.image} 
                            alt={service.name}
                            className="w-12 h-12 object-cover rounded-md"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary-100 rounded-md flex items-center justify-center">
                            <Search className="h-6 w-6 text-primary-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{service.name}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            <span className="truncate">{service.category}</span>
                            {service.price && (
                              <>
                                <span>•</span>
                                <span className="text-primary-600 font-semibold">₹{service.price}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === item.href
                    ? 'text-primary-600 font-bold border-b-2 border-primary-600'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-4">
              <Link to="/cart" className="flex items-center space-x-2 p-2 text-gray-600 hover:text-primary-600 relative">
                <ShoppingBag className="h-5 w-5" />
                <span className="text-sm font-medium">Cart</span>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              
              {/* User Account Dropdown */}
              <div 
                className="relative" 
                ref={userDropdownRef}
              >
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  {user?.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt={user.displayName || 'User'}
                      className="h-6 w-6 rounded-full object-cover border-2 border-white/30"
                    />
                  ) : (
                    <User className="h-5 w-5" />
                  )}
                  <span>Hello, {user?.displayName?.split(' ')[0] || 'Guest'}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 animate-fadeIn">
                    {user ? (
                      <>
                        <div className="px-4 py-3 border-b border-gray-200">
                          <div className="flex items-center space-x-3">
                            {user.photoURL ? (
                              <img 
                                src={user.photoURL} 
                                alt={user.displayName || 'User'}
                                className="h-10 w-10 rounded-full object-cover border-2 border-primary-200"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <UserCircle className="h-6 w-6 text-primary-600" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{user.displayName || 'User'}</p>
                              <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                          </div>
                        </div>
                        <Link
                          to="/account?tab=profile"
                          onClick={() => setShowUserDropdown(false)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <UserCircle className="h-4 w-4 mr-3" />
                          My Profile
                        </Link>
                        <Link
                          to="/account?tab=orders"
                          onClick={() => setShowUserDropdown(false)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <ShoppingBag className="h-4 w-4 mr-3" />
                          My Bookings
                        </Link>
                        <hr className="my-2" />
                        <button
                          onClick={() => {
                            setShowUserDropdown(false);
                            handleLogout();
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          onClick={() => setShowUserDropdown(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Login
                        </Link>
                        <Link
                          to="/signup"
                          onClick={() => setShowUserDropdown(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Sign Up
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </nav>

          {/* Mobile menu button - HIDDEN, replaced with bottom nav */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="hidden p-2 text-gray-700 hover:text-primary-600 transition-colors relative z-50"
          >
            <motion.div
              animate={{ rotate: isMenuOpen ? 90 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </motion.div>
          </button>

          {/* Spacer to push cart to right on mobile */}
          <div className="flex-1 md:hidden"></div>

          {/* Mobile Cart Button - Extreme Right */}
          <Link to="/cart" className="md:hidden relative text-gray-700 hover:text-primary-600 transition-colors">
            <ShoppingBag className="h-6 w-6" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {cartCount > 9 ? '9+' : cartCount}
              </span>
            )}
          </Link>
        </div>

        {/* Mobile Search - Hidden when menu is open */}
        {!isMenuOpen && (
          <div className="md:hidden pb-4">
            <div className="relative" ref={searchRef}>
              <input
                type="text"
                placeholder="Search for beauty, cleaning, repairs..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSearchSuggestions(true);
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                onFocus={() => setShowSearchSuggestions(true)}
                className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              />
              <button
                onClick={() => handleSearch()}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white p-2 rounded-md hover:bg-gray-700"
              >
                <Search className="h-4 w-4" />
              </button>

              {/* Mobile Search Suggestions */}
              {showSearchSuggestions && searchTerm && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl mt-1 z-50 max-h-96 overflow-y-auto">
                  {searchSuggestions.map((suggestionName, index) => {
                    const service = allServices.find(s => s.name === suggestionName);
                    return service ? (
                      <button
                        key={index}
                        data-suggestion="true"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSuggestionClick(suggestionName);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors border-b border-gray-100 last:border-b-0 first:rounded-t-lg last:rounded-b-lg flex items-center gap-3"
                      >
                        {service.image ? (
                          <img 
                            src={service.image} 
                            alt={service.name}
                            className="w-12 h-12 object-cover rounded-md"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary-100 rounded-md flex items-center justify-center">
                            <Search className="h-6 w-6 text-primary-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{service.name}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            <span className="truncate">{service.category}</span>
                            {service.price && (
                              <>
                                <span>•</span>
                                <span className="text-primary-600 font-semibold">₹{service.price}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="md:hidden bg-gradient-to-br from-white via-primary-50/30 to-purple-50/30 border-t border-gray-200 shadow-lg overflow-hidden"
          >
            <div className="px-4 py-4 space-y-2">
              {/* Navigation Links with staggered animation */}
              {navigation.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                >
                  <Link
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-4 py-3 text-base font-medium rounded-xl transition-all ${
                      location.pathname === item.href
                        ? 'text-white bg-gradient-to-r from-primary-600 to-purple-600 shadow-md transform scale-[1.02]'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-white/80 hover:shadow-sm'
                    }`}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}
              
              {/* User Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
                className="pt-4 space-y-3 border-t border-gray-200 mt-4"
              >
                {user ? (
                  <>
                    <div className="px-4 py-4 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl shadow-lg text-white">
                      <div className="flex items-center space-x-3">
                        {user.photoURL ? (
                          <img 
                            src={user.photoURL} 
                            alt={user.displayName || 'User'}
                            className="h-10 w-10 rounded-full object-cover border-2 border-white/30"
                          />
                        ) : (
                          <div className="bg-white/20 backdrop-blur-sm p-2 rounded-full">
                            <UserCircle className="h-6 w-6 text-white" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-bold">{user.displayName || 'User'}</p>
                          <p className="text-xs text-white/80 truncate">{user.email}</p>
                        </div>
                      </div>
                    </div>
                    
                    <Link
                      to="/account?tab=profile"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center px-4 py-3 text-gray-700 hover:bg-white/80 rounded-xl transition-all hover:shadow-sm group"
                    >
                      <div className="bg-primary-100 p-2 rounded-lg mr-3 group-hover:bg-primary-200 transition-colors">
                        <UserCircle className="h-5 w-5 text-primary-600" />
                      </div>
                      <span className="font-medium">My Profile</span>
                    </Link>
                    
                    <Link
                      to="/account?tab=orders"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center px-4 py-3 text-gray-700 hover:bg-white/80 rounded-xl transition-all hover:shadow-sm group"
                    >
                      <div className="bg-purple-100 p-2 rounded-lg mr-3 group-hover:bg-purple-200 transition-colors">
                        <ShoppingBag className="h-5 w-5 text-purple-600" />
                      </div>
                      <span className="font-medium">My Bookings</span>
                    </Link>
                    
                    <Link
                      to="/cart"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center px-4 py-3 text-gray-700 hover:bg-white/80 rounded-xl transition-all hover:shadow-sm group relative"
                    >
                      <div className="bg-blue-100 p-2 rounded-lg mr-3 group-hover:bg-blue-200 transition-colors">
                        <ShoppingBag className="h-5 w-5 text-blue-600" />
                      </div>
                      <span className="font-medium">Cart</span>
                      {cartCount > 0 && (
                        <span className="ml-auto bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full px-3 py-1 font-bold shadow-md">
                          {cartCount}
                        </span>
                      )}
                    </Link>
                    
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all hover:shadow-sm group"
                    >
                      <div className="bg-red-100 p-2 rounded-lg mr-3 group-hover:bg-red-200 transition-colors">
                        <LogOut className="h-5 w-5 text-red-600" />
                      </div>
                      <span className="font-medium">Logout</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setIsMenuOpen(false)}
                      className="block bg-gradient-to-r from-primary-600 to-purple-600 text-white text-center px-4 py-3 rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02] font-medium"
                    >
                      Login
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setIsMenuOpen(false)}
                      className="block bg-white border-2 border-primary-600 text-primary-600 text-center px-4 py-3 rounded-xl hover:bg-primary-50 transition-all shadow-sm hover:shadow-md transform hover:scale-[1.02] font-medium"
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Account Dialog */}
      <MobileAccountDialog 
        isOpen={showMobileAccountDialog} 
        onClose={() => setShowMobileAccountDialog(false)} 
      />
    </header>
    
    {/* Categories Navigation Bar */}
    <CategoryNavbar />
    </>
  );
}
